import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  computed,
  effect,
  inject,
  signal,
  untracked,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { CardModule } from '@openng/optimus-ui/card';
import { TableModule, TableLazyLoadEvent } from '@openng/optimus-ui/table';
import { TagModule } from '@openng/optimus-ui/tag';
import { TabsModule } from '@openng/optimus-ui/tabs';
import { InputTextModule } from '@openng/optimus-ui/inputtext';
import { SelectModule } from '@openng/optimus-ui/select';
import { ChartModule } from '@openng/optimus-ui/chart';
import { SkeletonModule } from '@openng/optimus-ui/skeleton';
import { LlmService } from '../../services/llm.service';
import { ThemeService } from '../../services/theme.service';
import { UiStateService } from '../../services/ui-state.service';
import { ChartData, ChartOptions, PeriodOption } from '../../models/dashboard.types';
import { LlmFilters } from '../../models/llm.types';
import { debounceTime, Subject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DateRangeSelectorComponent } from '../shared/date-range-selector.component';
import { ErrorBannerComponent } from '../shared/error-banner.component';

@Component({
  selector: 'app-llm',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    CardModule,
    TableModule,
    TagModule,
    TabsModule,
    InputTextModule,
    SelectModule,
    ChartModule,
    SkeletonModule,
    DateRangeSelectorComponent,
    ErrorBannerComponent,
  ],
  templateUrl: './llm.component.html',
  styleUrl: './llm.component.scss',
})
export class LlmComponent {
  protected llmService = inject(LlmService);
  protected uiState = inject(UiStateService);
  private router = inject(Router);
  private themeService = inject(ThemeService);
  private destroyRef = inject(DestroyRef);

  protected selectedPeriod = this.uiState.selectedPeriod;
  protected activeTab = signal<string | number | undefined>('analysis');
  protected startDate = this.uiState.startDate;
  protected endDate = this.uiState.endDate;

  protected filters = signal<LlmFilters>({ search: '', provider: '' });
  protected loading = signal(false);
  protected loadingCosts = signal(false);
  private searchSubject = new Subject<void>();

  protected providerOptions = [
    { label: 'OpenAI', value: 'openai' },
    { label: 'Google', value: 'google' },
  ];

  private blueColor = '#3b82f6';
  private greenColor = '#10b981';

  private baseChartOptions = computed<ChartOptions>(() => {
    const palette = this.themeService.chartPalette();
    return {
      maintainAspectRatio: false,
      plugins: {
        legend: { labels: { color: palette.text } },
      },
      scales: {
        x: {
          ticks: { color: palette.textSecondary, font: { weight: 500 } },
          grid: { color: palette.surfaceBorder, drawBorder: false },
        },
        y: {
          ticks: { color: palette.textSecondary },
          grid: { color: palette.surfaceBorder, drawBorder: false },
        },
      },
    };
  });

  protected tokenChartOptions = computed<ChartOptions>(() => ({
    ...this.baseChartOptions(),
    aspectRatio: 0.8,
  }));

  protected providerCostChartOptions = computed<ChartOptions>(() => ({
    ...this.baseChartOptions(),
    aspectRatio: 2,
  }));

  protected tokenChartData = computed<ChartData>(() => {
    const topPrompts = this.llmService.tokenAnalysis().slice(0, 10);
    return {
      labels: topPrompts.map((i) => i.promptName),
      datasets: [
        {
          label: 'Avg Input Tokens',
          backgroundColor: this.blueColor,
          data: topPrompts.map((i) => i.avgInputTokens),
        },
        {
          label: 'Avg Output Tokens',
          backgroundColor: this.themeService.chartPalette().primary,
          data: topPrompts.map((i) => i.avgOutputTokens),
        },
      ],
    };
  });

  protected totalProviderCost = computed(
    () => Number(this.llmService.providerCosts()?.totalCost) || 0,
  );

  protected providerCurrency = computed(() => this.llmService.providerCosts()?.currency ?? 'USD');

  protected providerCostChartData = computed<ChartData>(() => {
    const dailyCosts = this.llmService.providerCosts()?.dailyCosts ?? [];
    return {
      labels: dailyCosts.map((item) =>
        new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
      ),
      datasets: [
        {
          label: 'Daily Cost',
          data: dailyCosts.map((item) => Number(item.cost) || 0),
          borderColor: this.greenColor,
          backgroundColor: 'rgba(16, 185, 129, 0.1)',
          fill: true,
          tension: 0.4,
        },
      ],
    };
  });

  constructor() {
    effect(() => {
      this.uiState.startDate();
      this.uiState.endDate();
      untracked(() => this.loadData());
    });

    this.searchSubject.pipe(debounceTime(500), takeUntilDestroyed()).subscribe(() => {
      this.loadLogs({ first: 0, rows: 20 });
    });
  }

  loadData() {
    if (this.activeTab() === 'logs') {
      this.loadLogs({ first: 0, rows: 20 });
      return;
    }

    const { startDate, endDate } = this.getDateRange();
    this.loadingCosts.set(true);
    this.llmService
      .loadTokenAnalysis(startDate, endDate)
      // Cancels on destroy: LlmService state is root-scoped, so a response from a previous
      // visit to this screen would otherwise overwrite what the next visit already shows.
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.loadingCosts.set(false),
        error: () => this.loadingCosts.set(false),
      });
  }

  loadLogs(event: TableLazyLoadEvent) {
    this.loading.set(true);
    const { startDate, endDate } = this.getDateRange();
    const rows = event.rows ?? 20;
    const page = (event.first || 0) / rows;
    this.llmService
      .loadLlmLogs(startDate, endDate, page, rows, this.filters())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => this.loading.set(false),
        error: () => this.loading.set(false),
      });
  }

  onPeriodChange(period: PeriodOption) {
    this.uiState.setPeriod(period);
  }

  onDateRangeChange(dateRange: { startDate: Date; endDate: Date }) {
    this.uiState.setRange(dateRange.startDate, dateRange.endDate);
  }

  onTabChange() {
    this.loadData();
  }

  onFilterChange() {
    this.searchSubject.next();
  }

  openOrg(organisationId: number | null) {
    if (organisationId != null) {
      this.router.navigate(['/organizations', organisationId]);
    }
  }

  openUserSessions(userId: number | null) {
    if (userId != null) {
      this.uiState.setSessionUserFilter(userId);
      this.router.navigate(['/sessions']);
    }
  }

  private getDateRange(): { startDate: Date; endDate: Date } {
    return this.uiState.getRange();
  }
}
